import { ArrowCcwIcon, DownArrowIcon, FlagIcon, QuestionnaireIcon, TrashIcon, WriteIcon } from "@xola/icons";
import { Button, Dot, InlineValuePopover, Input, PopoverList, Spinner, Table } from "@xola/ui-kit";
import clsx from "clsx";
import {
    getDemographicCatalog,
    getQuestionnaireStatusIconColor,
    isEquipmentItem,
    isOrganizerOnlyForm,
    QuantitySpinner,
} from "common";
import { produce } from "immer";
import { partition, sortBy, sumBy, uniqueId } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import SimpleBar from "simplebar-react";
import {
    CatalogItem,
    CatalogItemObject,
    EquipmentCatalogItem,
    Experience,
    ExperiencePurchaseItem,
    Guest,
    GuestNameSource,
    LineItem,
    LineItemType,
    OPEN_UNLIMITED,
    Package,
    Permission,
    ProductCatalogItem,
    Timeslot,
    TimeslotUnit,
    UnitType,
} from "types";
import { InlineConfirmDeletePopover } from "../../Common/InlineConfirmDeletePopover";
import { NoTranslate } from "../../Common/NoTranslate";
import { getTimeslotUnit, GuestPayload } from "./AddGuest";
import { AutoTooltipContainer } from "@/components/Common/AutoTooltipContainer";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useVisibility } from "@/hooks/useVisibility";
import { getCatalogItemByTemplateId, getCatalogItemsByUnitType } from "@/models/Catalog";
import { isDefaultDemographic } from "@/models/DemographicUnit";
import { getMaxAttendees, hasUniqueGuestIdentification } from "@/models/Experience";
import {
    getIdentifiedGuests,
    getLastGuestCount,
    getUnIdentifiedGuests,
    hasLinkedWaiver,
    isSystemGeneratedName,
    sortGuestsByDisplayOrder,
} from "@/models/Guest";
import { getPackageDemographicCatalog, isSharedDemographicsPackage } from "@/models/Package";

const MIN_GUEST_ERROR_MESSAGE =
    "Named guests or Main Attendee can be removed using the trash button from the table below";

interface DemographicListProps {
    experience: Experience;
    timeslot: Timeslot;
    updatedGuests: GuestPayload[];
    onChange: (guests: Guest[] | GuestPayload[]) => void;
    guests: Guest[];
    isLoading: boolean;
    isAttendee?: boolean;
    package_?: Package;
    item: ExperiencePurchaseItem;
    onEquipmentChange?: (lineItems: any[]) => void;
    equipmentLineItems?: any[];
}

export const DemographicList = ({
    timeslot,
    experience,
    onChange,
    updatedGuests,
    guests,
    isLoading,
    isAttendee,
    package_,
    item,
    onEquipmentChange,
    equipmentLineItems,
}: DemographicListProps) => {
    const { isGranted } = useAuthorization();
    const [tableId] = useState(uniqueId("change-guest-table"));
    const total = updatedGuests.filter((g: GuestPayload) => !g._remove).length;
    const showWaiver = isGranted(Permission.WAIVERS_VIEW);

    const { data: demographicCatalogItems } = useAutoFetchCollection<ProductCatalogItem>("catalogItems", {
        unitType: UnitType.DEMOGRAPHIC,
        object: CatalogItemObject.GLOBAL_CATALOG_ITEM,
    });

    const { orderMax = OPEN_UNLIMITED, outingMax = OPEN_UNLIMITED } = experience?.group ?? {};
    const { open: openTimeslotCapacity, max: maxTimeslotCapacity } = timeslot?.capacity ?? {};

    const isExistOpenTimeslotCapacity =
        openTimeslotCapacity || (openTimeslotCapacity === 0 && maxTimeslotCapacity !== OPEN_UNLIMITED);

    const maxGuests = maxTimeslotCapacity ?? Math.max(Math.max(orderMax, outingMax), openTimeslotCapacity ?? 0);
    const availabilityGuests = isExistOpenTimeslotCapacity ? openTimeslotCapacity : maxGuests - total;

    const [availability, setAvailability] = useState(availabilityGuests);

    const max = useMemo(
        () => (isExistOpenTimeslotCapacity ? total + availability : maxGuests),
        [availability, total, isExistOpenTimeslotCapacity, maxGuests],
    );

    const [isAddingDemographic, setIsAddingDemographic] = useState(false);
    const isAttendeeUnique = hasUniqueGuestIdentification(experience);
    // we need to show the guests list even when item has signed waivers though unique attendee identification is turned off
    // so that seller can remove the guests by clicking on name, otherwise the first few guests will be removed.
    const hasSignedWaivers = showWaiver && Boolean(item.waivers?.length);
    const showGuestList = isAttendeeUnique || hasSignedWaivers;

    const handleDemographicChange = (newCatalogItem: CatalogItem, guest: GuestPayload, index: number) => {
        const lastCatalogItem = guest.template?.id
            ? getCatalogItemByTemplateId(catalogs, guest.template.id)
            : newCatalogItem;
        const isSystemName = lastCatalogItem ? isSystemGeneratedName(guest, lastCatalogItem) : false;
        const count = getGuestQuantity(guests, newCatalogItem, timeslot.units);
        const lastGuestCount = getLastGuestCount(count, newCatalogItem.name, guests);
        const name = isSystemName ? `${newCatalogItem.name} #${lastGuestCount + 1}` : guest.name;

        onChange(
            updatedGuests.map((testGuest, testIndex) => {
                if (index === testIndex) {
                    const unit = getTimeslotUnit(newCatalogItem, timeslot.units);
                    if (unit && newCatalogItem.template?.id) {
                        return {
                            ...testGuest,
                            name: name,
                            unit: { id: unit.id },
                            template: { id: newCatalogItem.template.id },
                        };
                    }
                }

                return testGuest;
            }),
        );
    };

    const handleGuestNameChange = (name: string, guest: Guest | GuestPayload, index: number) => {
        onChange(
            updatedGuests.map((testGuest, testIndex) =>
                testIndex === index ? { ...testGuest, name, source: GuestNameSource.SELLER } : testGuest,
            ),
        );
    };

    const handleDeleteGuest = (index: number) => {
        const newGuests = produce(updatedGuests, (guestsDraft: GuestPayload[]) => {
            guestsDraft[index]._remove = true;
        });
        onChange(newGuests);

        if (maxGuests !== OPEN_UNLIMITED) {
            setAvailability((availability) => availability + 1);
        }
    };

    const handleRestoreGuest = (index: number) => {
        const newGuests = produce(updatedGuests, (guestsDraft: GuestPayload[]) => {
            delete guestsDraft[index]._remove;
        });
        onChange(newGuests);
        setAvailability((availability) => availability - 1);
    };

    const handleAddGuest = (value: number, catalog: CatalogItem) => {
        const unit = getTimeslotUnit(catalog, timeslot.units);
        if (unit) {
            const guestsForCatalog = filterGuestsByUnit(guests, catalog, timeslot.units);
            const guestLength = guestsForCatalog.length;

            if (value > guestLength) {
                setIsAddingDemographic(true);
                // Increase guest quantity
                const guestAddCount = value - guestLength;
                if (guestAddCount) {
                    const newGuests = produce(guests, (guestsDraft: GuestPayload[]) => {
                        const count = getGuestQuantity(guests, catalog, timeslot.units);
                        const lastGuestCount = getLastGuestCount(count, catalog.name, guests);
                        for (let index = 1; index <= guestAddCount; index++) {
                            guestsDraft.unshift({
                                name: `${catalog.name} #${lastGuestCount + index}`,
                                unit: { id: unit.id },
                                source: GuestNameSource.DEFAULT,
                            });
                        }
                    });
                    onChange(newGuests);

                    if (maxGuests !== OPEN_UNLIMITED) {
                        setAvailability((availability) => availability - guestAddCount);
                    }
                }
            } else if (guestLength > value) {
                setIsAddingDemographic(true);
                // decrease guest quantity
                const guestRemoveCount = guestLength - value;
                if (guestRemoveCount) {
                    const newGuests = produce(guests, (guestsDraft: GuestPayload[]) => {
                        const filteredGuests = filterGuestsByUnit(guestsDraft, catalog, timeslot.units);
                        const removableGuests = isAttendeeUnique
                            ? getUnIdentifiedGuests(filteredGuests, demographicCatalogItems)
                            : filteredGuests;

                        const bottomUpGuests = sortGuestsByDisplayOrder(removableGuests).reverse();
                        const [signedGuests, unsignedGuests] = partition(bottomUpGuests, hasLinkedWaiver);
                        const removalOrder = [...unsignedGuests, ...signedGuests];
                        for (const guest of removalOrder.slice(0, guestRemoveCount)) {
                            guest._remove = true;
                        }
                    });

                    onChange(newGuests);

                    if (maxGuests !== OPEN_UNLIMITED) {
                        setAvailability((availability) => availability + guestRemoveCount);
                    }
                }
            }
        }
    };

    const catalogs =
        package_ && isSharedDemographicsPackage(package_)
            ? getPackageDemographicCatalog(package_)
            : isEquipmentItem(item)
            ? getCatalogItemsByUnitType(experience.catalog?.items ?? [], UnitType.DEMOGRAPHIC_SECONDARY)
            : getDemographicCatalog(experience);

    useEffect(() => {
        setIsAddingDemographic(false);
    }, [total, max]);

    useEffect(() => {
        // When user is attendee we should use capacity.open number for availability amount,
        // but when the change guest modal is opened this value equials 0. So it needs to add a condition to separate this cases.
        const availabilityValue = isAttendee ? timeslot.capacity.open : max - total;
        setAvailability(availabilityValue);
    }, [timeslot, max, isAttendee, total]);

    useEffect(() => {
        setAvailability(availabilityGuests);
    }, [availabilityGuests]);

    return (
        <div className="flex flex-col justify-between space-y-10" id={tableId}>
            {isEquipmentItem(item) && (
                <EquipmentQuantitySection
                    experience={experience}
                    timeslot={timeslot}
                    item={item}
                    updatedGuests={updatedGuests}
                    equipmentLineItems={equipmentLineItems}
                    demographicCatalogItems={demographicCatalogItems}
                    isAttendeeUnique={isAttendeeUnique}
                    onEquipmentChange={onEquipmentChange}
                    onAttendeeChange={handleAddGuest}
                />
            )}
            {!isEquipmentItem(item) && (
                <div className="!mt-10 space-y-1">
                    <div className="p1 font-bold">Guest Summary</div>
                    <div className="space-y-1 text-gray-darker">
                        {sortBy(catalogs, "sequence").map((demographic) => {
                            const guestsForCatalog = filterGuestsByUnit(guests, demographic, timeslot.units);
                            const minGuestCount = isAttendeeUnique
                                ? getIdentifiedGuests(guestsForCatalog, demographicCatalogItems).length
                                : 0;

                            return (
                                <div key={`summary-${demographic.id}`} className="flex justify-between">
                                    <NoTranslate
                                        if={!isDefaultDemographic(demographic.name)}
                                        as="div"
                                        className="min-w-0 break-all"
                                    >
                                        {demographic.name}
                                    </NoTranslate>
                                    {isAttendee ? (
                                        <DemographicCount
                                            count={getGuestQuantity(updatedGuests, demographic, timeslot.units)}
                                        />
                                    ) : (
                                        <div className="shrink-0">
                                            <QuantitySpinner
                                                allowEditManually
                                                value={getGuestQuantity(updatedGuests, demographic, timeslot.units)}
                                                total={total}
                                                min={total === 1 ? 1 : minGuestCount}
                                                minErrorMessage={
                                                    minGuestCount > 0 ? MIN_GUEST_ERROR_MESSAGE : undefined
                                                }
                                                manualMinErrorMessage={getManualMinErrorMessage({
                                                    minGuestCount,
                                                    name: demographic.name,
                                                    isAttendeeUnique,
                                                })}
                                                max={max}
                                                size="medium"
                                                className="mr-3"
                                                classNames={{ label: "font-bold" }}
                                                onChange={(value) => handleAddGuest(value, demographic)}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <AvailabilityDetail
                        max={timeslot.capacity.max}
                        availability={availability}
                        total={total}
                        isAttendee={isAttendee}
                    />
                </div>
            )}

            {showGuestList && (total > 0 || !isEquipmentItem(item)) && (
                <SimpleBar style={{ maxHeight: 340, minHeight: 306 }}>
                    <Table>
                        <Table.Head>
                            <Table.Row>
                                <Table.Cell className="font-bold">Name</Table.Cell>
                                <Table.Cell className="font-bold">
                                    Demographic
                                    {isAddingDemographic && <Spinner size="tiny" />}
                                </Table.Cell>
                            </Table.Row>
                        </Table.Head>

                        <Table.Body>
                            {updatedGuests.map((guest: GuestPayload, index) => {
                                const key = guest.id ?? `${guest.name}${guest.unit.id}${index}`;
                                const isGuestRemoved = guest._remove;

                                return (
                                    <Table.Row
                                        key={key}
                                        className="border border-gray-lighter"
                                        data-testid="demographic-list-item"
                                    >
                                        <Table.Cell
                                            className={clsx(
                                                isGuestRemoved && "text-red line-through",
                                                !guest.id && "text-blue",
                                                "max-w-[130px] flex-1  self-center py-3 px-4",
                                            )}
                                        >
                                            <DemographicName
                                                parentId={tableId}
                                                guest={guest}
                                                isGuestRemoved={isGuestRemoved}
                                                onChange={(guestName) => handleGuestNameChange(guestName, guest, index)}
                                            />
                                        </Table.Cell>
                                        <Table.Cell
                                            className={clsx(
                                                isGuestRemoved && "text-red line-through",
                                                !guest.id && "text-blue",
                                                "max-w-[200px] flex-1 self-center  py-3 px-4",
                                            )}
                                        >
                                            <DemographicChoice
                                                catalog={catalogs}
                                                isGuestRemoved={isGuestRemoved}
                                                value={getGuestCatalog(guest, catalogs, timeslot.units)}
                                                parentId={tableId}
                                                isAttendee={isAttendee}
                                                guestCount={total}
                                                item={item}
                                                guest={guest}
                                                isLoading={isLoading}
                                                showWaiver={showWaiver}
                                                onChange={(demographics) =>
                                                    handleDemographicChange(demographics, guest, index)
                                                }
                                                onDelete={() => handleDeleteGuest(index)}
                                                onRestore={() => handleRestoreGuest(index)}
                                            />
                                        </Table.Cell>
                                    </Table.Row>
                                );
                            })}
                        </Table.Body>
                    </Table>
                </SimpleBar>
            )}
        </div>
    );
};

const getManualMinErrorMessage = ({
    minGuestCount,
    name,
    isAttendeeUnique,
}: {
    minGuestCount: number;
    name: string;
    isAttendeeUnique: boolean;
}) => {
    return (
        <span>
            You can only reduce up to {minGuestCount} <NoTranslate>{name}</NoTranslate> from here.{" "}
            {isAttendeeUnique && `For specific ${(<NoTranslate>{name}</NoTranslate>)}, remove them from the list below`}
        </span>
    );
};

interface DemographicChoiceProps {
    value?: CatalogItem;
    catalog: CatalogItem[];
    parentId: string;
    guestCount: number;
    onChange: (catalog: CatalogItem) => void;
    onDelete: () => void;
    onRestore: () => void;
    item: ExperiencePurchaseItem;
    showWaiver: boolean;
    isAttendee?: boolean;
    isLoading: boolean;
    isGuestRemoved?: boolean;
    guest: GuestPayload;
}

const DemographicChoice = ({
    value,
    catalog,
    parentId,
    guestCount,
    isGuestRemoved,
    onChange,
    onDelete,
    onRestore,
    showWaiver,
    isAttendee,
    isLoading,
    item,
    guest,
}: DemographicChoiceProps) => {
    const [visible, { toggle }] = useVisibility(false);

    const toggleVisible = () => {
        if (!isLoading) {
            toggle();
        }
    };

    const handleCatalogItemClick = (catalog: CatalogItem) => {
        if (!isLoading) {
            onChange(catalog);
            toggle();
        }
    };

    const isItemLevelQuestionnaire = isOrganizerOnlyForm(item);
    const { form } = item;
    const linkedWaiverParticipant = showWaiver ? !!guest.waiver?.id && !!guest.participant : null;
    const shouldTranslate = isDefaultDemographic(value?.name ?? "");

    return (
        <div className="flex items-center" data-testid="demographic-list-demographic-type">
            <div className="flex flex-grow items-center space-x-1">
                <span className="flex">
                    {form && !isItemLevelQuestionnaire && (
                        <span className="mr-1 flex items-center">
                            <Dot size="small" color={getQuestionnaireStatusIconColor(form, guest as Guest)} />
                            <QuestionnaireIcon />
                        </span>
                    )}
                    {showWaiver && (
                        <span className="flex items-center">
                            <Dot size="small" color={linkedWaiverParticipant ? "success" : "danger"} />
                            <WriteIcon />
                        </span>
                    )}
                </span>
                {isGuestRemoved || catalog.length === 1 ? (
                    <div className="max-w-[100px]">
                        <AutoTooltipContainer tooltipContent={value?.name ?? ""}>
                            <span
                                className={clsx(
                                    "font-bold",
                                    isGuestRemoved && "line-through",
                                    !shouldTranslate && "no-translate",
                                )}
                            >
                                {value?.name ?? ""}
                            </span>
                        </AutoTooltipContainer>
                    </div>
                ) : (
                    <div>
                        <PopoverList
                            visible={visible}
                            appendTo={() => document.querySelector(`#${parentId}`)}
                            onClickOutside={toggle}
                        >
                            <span
                                className={clsx(
                                    "inline-flex cursor-pointer items-center space-x-2",
                                    isLoading && "cursor-default",
                                )}
                                onClick={toggleVisible}
                            >
                                <div className="max-w-[90px]">
                                    <AutoTooltipContainer tooltipContent={value?.name ?? ""}>
                                        <span
                                            className={clsx(
                                                "font-bold underline underline-offset-4",
                                                !shouldTranslate && "no-translate",
                                            )}
                                        >
                                            {value?.name ?? ""}
                                        </span>
                                    </AutoTooltipContainer>
                                </div>
                                <span>
                                    <DownArrowIcon size="medium" />
                                </span>
                            </span>
                            {catalog.map((catalogItem) =>
                                catalogItem.id === value?.id ? null : (
                                    <PopoverList.Item
                                        key={catalogItem.id}
                                        name={catalogItem.name}
                                        isActive={catalogItem.id === value?.id}
                                        onClickItem={() => handleCatalogItemClick(catalogItem)}
                                    >
                                        <NoTranslate if={!isDefaultDemographic(catalogItem.name)}>
                                            {catalogItem.name}
                                        </NoTranslate>
                                    </PopoverList.Item>
                                ),
                            )}
                        </PopoverList>
                    </div>
                )}
            </div>
            {guestCount > 1 && isAttendee === false && !isGuestRemoved ? (
                <InlineConfirmDeletePopover
                    placement="left"
                    confirmButtonText="Confirm"
                    appendTo={document.querySelector(`#${parentId}`)}
                    onConfirm={onDelete}
                >
                    <span role="button" className="cursor-pointer p-1">
                        <TrashIcon />
                    </span>
                </InlineConfirmDeletePopover>
            ) : null}
            {isGuestRemoved && isAttendee === false && (
                <span role="button" className="max-h-5 cursor-pointer p-1" onClick={onRestore}>
                    <ArrowCcwIcon />
                </span>
            )}
        </div>
    );
};

interface DemographicNameProps {
    guest: GuestPayload;
    onChange: (name: string) => void;
    parentId: string;
    isGuestRemoved?: boolean;
}

const DemographicName = ({ guest, isGuestRemoved, onChange, parentId }: DemographicNameProps) => {
    const { name } = guest;
    const [id] = useState(uniqueId("demographic-name"));
    const [visible, { toggle }] = useVisibility(false);
    const [text, setText] = useState(name);
    const [inputText, setInputText] = useState(name);

    useEffect(() => {
        if (visible) {
            const input = document.querySelector<HTMLElement>(`#${id}`); // TODO: Should use ref
            if (input) {
                input.focus();
            }
        }
    }, [visible, id]);

    useEffect(() => {
        setText(name);
        setInputText(name);
    }, [name]);

    const handleInputChange = (e: any) => {
        setInputText(e.target.value);
    };

    const handleApply = () => {
        setText(inputText);
        onChange(inputText);
        toggle();
    };

    const handleKeyUp = (e: any) => {
        if (e.keyCode === 13) {
            e.preventDefault();
            handleApply();
        }
    };

    return (
        <div className="relative" data-testid="demographic-list-demographic-name">
            <AutoTooltipContainer tooltipContent={text}>
                {guest.primary && <FlagIcon className="mr-1 -ml-4.5" />}
                {isGuestRemoved ? (
                    <NoTranslate className={clsx("font-bold", isGuestRemoved && "line-through")}>{text}</NoTranslate>
                ) : (
                    <InlineValuePopover
                        text={text}
                        isOpen={visible}
                        showArrow={false}
                        classNames={{
                            text: "!whitespace-normal no-translate",
                            textField: !guest.id && "border-b-blue text-blue",
                        }}
                        appendTo={() => document.querySelector(`#${parentId}`)}
                        onClick={toggle}
                        onClickOutside={toggle}
                    >
                        <div className="!m-0 flex flex-row space-x-2">
                            <Input
                                className="w-[235px]"
                                id={id}
                                value={inputText}
                                data-testid="demographic-list-demographic-name-input"
                                onChange={handleInputChange}
                                onKeyUp={handleKeyUp}
                            />
                            <Button onClick={handleApply}>Apply</Button>
                        </div>
                    </InlineValuePopover>
                )}
            </AutoTooltipContainer>
        </div>
    );
};

const DemographicCount = ({ count, name }: { count: number; name?: string }) => {
    return (
        <div className="flex justify-between py-1 pr-[58px] font-bold">
            {name ?? <div>{name}</div>}
            <div>{count}</div>
        </div>
    );
};

const AvailabilityDetail = ({
    max,
    availability,
    total,
    isAttendee,
}: {
    max: number;
    availability: number;
    total: number;
    isAttendee?: boolean;
}) => {
    return (
        <div className="flex flex-col divide-y divide-gray-light border-y border-gray-light">
            <DemographicCount name="Total" count={total} />
            {!isAttendee && max < OPEN_UNLIMITED && <DemographicCount name="Available" count={availability} />}
        </div>
    );
};

const getGuestQuantity = (guests: GuestPayload[], catalog: CatalogItem, units: TimeslotUnit[]): number => {
    const timeslotUnit = getTimeslotUnit(catalog, units);
    return sumBy(guests, (guest) => (guest.unit.id === timeslotUnit?.id && !guest._remove ? 1 : 0));
};

const getGuestCatalog = (guest: GuestPayload, catalog: CatalogItem[], units: TimeslotUnit[]) => {
    return catalog.find((item) => getTimeslotUnit(item, units)?.id === guest.unit.id);
};

const filterGuestsByUnit = (guests: GuestPayload[], catalog: CatalogItem, units: TimeslotUnit[]) => {
    const unit = getTimeslotUnit(catalog, units);
    if (unit) {
        return guests.filter((guest: GuestPayload) => !guest._remove && guest.unit.id === unit.id);
    }

    return [];
};

interface EquipmentQuantitySectionProps {
    experience: Experience;
    timeslot: Timeslot;
    item: ExperiencePurchaseItem;
    updatedGuests: GuestPayload[];
    onEquipmentChange?: (lineItems: any[]) => void;
    onAttendeeChange?: (value: number, catalog: CatalogItem) => void;
    equipmentLineItems?: any[];
    demographicCatalogItems: ProductCatalogItem[];
    isAttendeeUnique: boolean;
}

const EquipmentQuantitySection = ({
    experience,
    timeslot,
    item,
    updatedGuests,
    onEquipmentChange,
    onAttendeeChange,
    equipmentLineItems: externalLineItems,
    demographicCatalogItems,
    isAttendeeUnique,
}: EquipmentQuantitySectionProps) => {
    const equipmentItems = getCatalogItemsByUnitType<EquipmentCatalogItem>(
        experience.catalog?.items ?? [],
        UnitType.EQUIPMENT,
    );
    const attendeeItems = getCatalogItemsByUnitType(experience.catalog?.items ?? [], UnitType.DEMOGRAPHIC_SECONDARY);

    const currentLineItems = externalLineItems ?? item.lineItems ?? [];
    const originalLineItems = item.lineItems ?? [];

    const getQty = (type: LineItemType, templateId: string) =>
        currentLineItems.find((li: any) => li.type === type && li.template?.id === templateId)?.quantity ?? 0;

    const getOriginalQty = (type: LineItemType, templateId: string) =>
        originalLineItems.find((li: any) => li.type === type && li.template?.id === templateId)?.quantity ?? 0;

    const handleEquipmentQtyChange = (templateId: string, name: string, newQty: number) => {
        if (!onEquipmentChange) return;
        const updated = [...currentLineItems];
        const index = updated.findIndex(
            (li: any) => li.type === LineItemType.EQUIPMENT && li.template?.id === templateId,
        );
        if (index >= 0) {
            updated[index] = { ...updated[index], quantity: newQty };
        } else {
            updated.push({
                type: LineItemType.EQUIPMENT,
                quantity: newQty,
                template: { id: templateId },
                name,
                includeInAmount: true,
            });
        }
        onEquipmentChange(updated);
    };

    const maxAttendees = getMaxAttendees(experience, currentLineItems);

    const totalEquipment = sumBy(equipmentItems, (eq) => getQty(LineItemType.EQUIPMENT, eq.template?.id ?? ""));
    const totalAttendees = updatedGuests.filter((g) => !g._remove).length;
    const hasTooManyAttendees = maxAttendees > 0 && totalAttendees > maxAttendees;

    return (
        <div className="!mt-10 space-y-6">
            {/* Equipment quantity */}
            <div className="space-y-1">
                <div className="p1 font-bold">Quantity</div>
                <div className="space-y-1 text-gray-darker">
                    {sortBy(equipmentItems, "sequence").map((eq) => {
                        const unit = timeslot.units?.find((u: any) => u.template?.id === eq.template?.id);
                        const qty = getQty(LineItemType.EQUIPMENT, eq.template?.id ?? "");
                        const originalQty = getOriginalQty(LineItemType.EQUIPMENT, eq.template?.id ?? "");
                        const unitCount = unit?.count;
                        const open = unitCount != null ? originalQty + unitCount : originalQty;
                        return (
                            <div key={eq.id} className="flex items-center justify-between">
                                <NoTranslate as="div" className="min-w-0 break-all">
                                    {eq.name}
                                </NoTranslate>
                                <div className="shrink-0">
                                    <QuantitySpinner
                                        allowEditManually
                                        value={qty}
                                        min={totalEquipment === 1 ? 1 : 0}
                                        max={open}
                                        size="medium"
                                        className="mr-3"
                                        classNames={{ label: "font-bold" }}
                                        onChange={(value) =>
                                            handleEquipmentQtyChange(eq.template?.id ?? "", eq.name, value)
                                        }
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="flex flex-col divide-y divide-gray-light border-y border-gray-light">
                    <div className="flex justify-between py-1 pr-[58px] font-bold">
                        <div>Total</div>
                        <div>{totalEquipment}</div>
                    </div>
                </div>
            </div>

            {attendeeItems.length > 0 && (
                <div className="space-y-1">
                    <div className={clsx("p1 font-bold", hasTooManyAttendees && "text-danger")}>
                        Attendees{maxAttendees > 0 ? ` (Select up to ${maxAttendees})` : ""}
                    </div>
                    <div className="space-y-1 text-gray-darker">
                        {sortBy(attendeeItems, "sequence").map((attendee) => {
                            const attendeeGuests = filterGuestsByUnit(updatedGuests, attendee, timeslot.units);
                            const qty = attendeeGuests.length;
                            // Named attendees can only go via the trash button, so the spinner stops there
                            const minQty = isAttendeeUnique
                                ? getIdentifiedGuests(attendeeGuests, demographicCatalogItems).length
                                : 0;
                            return (
                                <div key={attendee.id} className="flex items-center justify-between">
                                    <NoTranslate as="div" className="min-w-0 break-all">
                                        {attendee.name}
                                    </NoTranslate>
                                    <div className="shrink-0">
                                        <QuantitySpinner
                                            allowEditManually
                                            value={qty}
                                            total={totalAttendees}
                                            min={minQty}
                                            minErrorMessage={minQty > 0 ? MIN_GUEST_ERROR_MESSAGE : undefined}
                                            manualMinErrorMessage={getManualMinErrorMessage({
                                                minGuestCount: minQty,
                                                name: attendee.name,
                                                isAttendeeUnique,
                                            })}
                                            max={maxAttendees > 0 ? maxAttendees : 99_999}
                                            size="medium"
                                            className="mr-3"
                                            classNames={{ label: "font-bold" }}
                                            onChange={(value) => onAttendeeChange?.(value, attendee)}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex flex-col divide-y divide-gray-light border-y border-gray-light">
                        <div className="flex justify-between py-1 pr-[58px] font-bold">
                            <div>Total</div>
                            <div>{totalAttendees}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
